import type {
  LeaderboardApiEntry,
  LeaderboardResponse,
} from 'hooks/workoutLeaderboard/useWorkoutLeaderboard';
import type { WorkoutLevelEnum } from 'types/enums';

// Below this many competitors, "Top X%" reads as nonsense (a lone athlete would see
// "Top 100%") rather than a meaningful stat — confirmed with Pablo (Slack, 2026-07-30):
// show the percentage once the pool hits 10, otherwise show just the rank.
const MINIMUM_ATHLETES_FOR_PERCENTILE = 10;

export interface MyRanking {
  entry: LeaderboardApiEntry;
  percentile: number | null;
  level: WorkoutLevelEnum | null;
}

// A leveled board ranks each level independently, so "my" percentile should be
// relative to my own level's entry count, not the whole board's total — a flat
// board (exactly one group) has no levels to separate, so total is the right
// denominator there instead. `level` is carried along so the UI can label which
// group a percentile is relative to (a leveled board's groups always have a
// level; a flat board's single group has level: null).
export const computeMyRanking = (
  leaderboard: LeaderboardResponse | undefined,
  myAthleteId: string | null,
): MyRanking | null => {
  if (!leaderboard || !myAthleteId) return null;
  for (const group of leaderboard.groups) {
    const entry = group.entries.find(e => e.athleteId === myAthleteId);
    if (!entry) continue;
    const denominator = leaderboard.groups.length > 1 ? group.entries.length : leaderboard.total;
    const percentile =
      denominator >= MINIMUM_ATHLETES_FOR_PERCENTILE ? Math.ceil((entry.rank / denominator) * 100) : null;
    return { entry, percentile, level: group.level };
  }
  return null;
};

// Used to tell "never logged" apart from "logged, but filtered out" for the
// not-ranked card — see AthleteLeaderboardScreen's hasLoggedAnyResult.
export const isAthleteInLeaderboard = (
  leaderboard: LeaderboardResponse | undefined,
  myAthleteId: string | null,
): boolean => {
  if (!leaderboard || !myAthleteId) return false;
  return leaderboard.groups.some(group => group.entries.some(entry => entry.athleteId === myAthleteId));
};
